import { ExternalLink } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DashboardStatsSkeleton } from '@/components/dashboard/dashboard-section-skeleton'
import { useObservabilityStatus } from '@/hooks/use-observability-status'

export function DeveloperDashboardSections() {
  const { data, isLoading, error } = useObservabilityStatus()
  const langsmith = data?.langsmith

  if (isLoading) {
    return <DashboardStatsSkeleton count={1} maxColumns={2} />
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
        <p className="font-medium text-destructive">Could not load LangSmith status</p>
        <p className="mt-1 text-muted-foreground">
          {(error as Error).message ?? 'Something went wrong.'}
        </p>
      </div>
    )
  }

  const connected = langsmith?.connected === true

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>LangSmith</CardTitle>
            <CardDescription>Agent tracing and evaluation observability</CardDescription>
          </div>
          <Badge variant={connected ? 'successSoft' : 'warningSoft'}>
            {connected ? 'Connected' : 'Not connected'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-1 gap-3 text-sm @2col:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Project</dt>
            <dd className="font-medium">{langsmith?.project?.trim() || '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Traces</dt>
            <dd className="font-medium">
              {typeof langsmith?.trace_count === 'number' ? langsmith.trace_count.toLocaleString() : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Runs</dt>
            <dd className="font-medium">
              {typeof langsmith?.run_count === 'number' ? langsmith.run_count.toLocaleString() : '—'}
            </dd>
          </div>
        </dl>
        {langsmith?.external_url ? (
          <Button variant="outline" size="sm" asChild>
            <a href={langsmith.external_url} target="_blank" rel="noopener noreferrer">
              Open in LangSmith
              <ExternalLink className="size-4" aria-hidden />
            </a>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}
